import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import PageLoader from "../../components/PageLoader/PageLoader";
import { publicApi } from "../../api/http";
import { authAxios, useAuth } from "../../contexts/AuthContext";
import { DraftYearContext } from "../../contexts/DraftYearContext";
import RequireAuth from "../../components/RequireAuth/RequireAuth";
import PlayerCard from "../../components/PlayerCard/PlayerCard";
import {
  ALL_TRAIT_KEYS,
  TRAIT_SCORE_MAX,
  TRAIT_SCORE_MIN,
  emptyTraitScores,
  isGoalie,
  traitFieldsForPlayer,
} from "./rankingTraitFields";
import RatingScaleGuide from "./RatingScaleGuide";
import { useWarnUnsavedChanges } from "./useWarnUnsavedChanges";
import { isRankingPublished } from "../../utils/independentRanking";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import "./CreateRanking.css";

const DRAFT_YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019];
const POINTER_DRAG_THRESHOLD = 6;

const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--text-muted)", fontSize: "0.8rem" },
  "& .MuiOutlinedInput-root": {
    color: "var(--text-primary)",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "var(--border-default)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--brand)" },
  },
};

const selectSx = {
  backgroundColor: "rgba(255, 255, 255, 0.04)",
  color: "var(--text-primary)",
  fontSize: "0.875rem",
  fontWeight: 600,
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-default)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-strong)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--brand)" },
  "& .MuiSvgIcon-root": { color: "var(--text-secondary)" },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      backgroundColor: "var(--bg-surface-2)",
      border: "1px solid var(--border-default)",
      borderRadius: "10px",
      "& .MuiMenuItem-root": {
        fontSize: "0.875rem",
        color: "var(--text-primary)",
        "&.Mui-selected": {
          backgroundColor: "rgba(126, 168, 98, 0.15)",
          color: "var(--brand-strong)",
        },
      },
    },
  },
};

const switchSx = {
  width: 36,
  height: 20,
  p: 0,
  "& .MuiSwitch-switchBase": {
    p: 0.25,
    transition: "transform 0.2s ease",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "var(--brand)",
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 16,
    height: 16,
    boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 10,
    backgroundColor: "#3a424d",
    opacity: 1,
  },
};

function makeEntry(player) {
  return {
    player_id: player.player_id,
    first_name: player.first_name,
    last_name: player.last_name,
    player,
    note: "",
    ...emptyTraitScores(),
  };
}

function entryFromRankingRow(row) {
  const player = row.player;
  const entry = makeEntry(player);
  entry.note = row.note || "";
  ALL_TRAIT_KEYS.forEach((key) => {
    if (row[key] != null && row[key] !== "") {
      entry[key] = String(row[key]);
    }
  });
  return entry;
}

function formSnapshot(title, description, rankingDraftYear, includeNotes, includeAttributes, entries) {
  return JSON.stringify({
    title: title.trim(),
    description: description.trim(),
    rankingDraftYear,
    includeNotes,
    includeAttributes,
    entries: entries.map((e) => ({
      player_id: e.player_id,
      note: e.note || "",
      ...Object.fromEntries(ALL_TRAIT_KEYS.map((key) => [key, e[key] ?? ""])),
    })),
  });
}

export function CreateRankingContent({ rankingId = null }) {
  const isEdit = Boolean(rankingId);
  const { accessToken } = useAuth();
  const { draftYear: siteDraftYear } = useContext(DraftYearContext);
  const navigate = useNavigate();

  const [rankingDraftYear, setRankingDraftYear] = useState(siteDraftYear);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includeAttributes, setIncludeAttributes] = useState(false);
  const [entries, setEntries] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchCache, setSearchCache] = useState([]);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const entryRefs = useRef({});
  const dragIndexRef = useRef(null);
  const dragOverIndexRef = useRef(null);
  const touchPointerRef = useRef({
    active: false,
    fromIndex: null,
    pointerId: null,
    startX: 0,
    startY: 0,
    moved: false,
  });
  const [saving, setSaving] = useState(false);
  const [saveMode, setSaveMode] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadingRanking, setLoadingRanking] = useState(isEdit);
  const [loadError, setLoadError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [mobileScaleGuideOpen, setMobileScaleGuideOpen] = useState(true);
  const loadedSnapshotRef = useRef(null);

  useEffect(() => {
    if (!isEdit) {
      setRankingDraftYear(siteDraftYear);
    }
  }, [siteDraftYear, isEdit]);

  useEffect(() => {
    if (!isEdit || !rankingId || !accessToken) return;

    let cancelled = false;
    setLoadingRanking(true);
    setLoadError("");
    authAxios
      .get(`/independent_rankings/${rankingId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        if (isRankingPublished(data)) {
          setLoadError(
            "This ranking has been published and cannot be edited or deleted."
          );
          return;
        }
        const nextTitle = data.title || "";
        const nextDescription = data.description || "";
        const nextYear = Number(data.draft_year) || siteDraftYear;
        const rows = [...(data.rankings || [])].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
        const nextIncludeNotes = rows.some((r) => Boolean(r.note?.trim()));
        const nextIncludeAttributes = rows.some((r) =>
          ALL_TRAIT_KEYS.some((key) => r[key] != null && r[key] !== "")
        );
        const nextEntries = rows.map((row) => entryFromRankingRow(row));

        setTitle(nextTitle);
        setDescription(nextDescription);
        setRankingDraftYear(nextYear);
        setIncludeNotes(nextIncludeNotes);
        setIncludeAttributes(nextIncludeAttributes);
        setEntries(nextEntries);
        loadedSnapshotRef.current = formSnapshot(
          nextTitle,
          nextDescription,
          nextYear,
          nextIncludeNotes,
          nextIncludeAttributes,
          nextEntries
        );
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(
            "Could not load this ranking. It may have been deleted or you may not have access."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRanking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, rankingId, accessToken, siteDraftYear]);

  const rankedIds = new Set(entries.map((e) => e.player_id));

  const sortPlayerOptions = (list) =>
    [...list].sort((a, b) =>
      `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)
    );

  const fetchSearchOptions = async (value, year = rankingDraftYear) => {
    if (value.length < 3) {
      setSearchOptions([]);
      return;
    }
    try {
      const { data } = await publicApi.get(`/players/search/${value}`, {
        params: { draft_year: year },
      });
      const sorted = sortPlayerOptions(data || []);
      setSearchCache(sorted);
      setSearchOptions(sorted.filter((p) => !rankedIds.has(p.player_id)));
    } catch {
      setSearchOptions([]);
    }
  };

  useEffect(() => {
    setSearchCache([]);
    setSearchOptions([]);
    if (searchInput.length >= 3) {
      fetchSearchOptions(searchInput, rankingDraftYear);
    }
  }, [rankingDraftYear]);

  const handleSearchInput = (_, value, reason) => {
    if (reason === "input") {
      setSearchInput(value);
      if (value.length === 3) {
        fetchSearchOptions(value);
      } else if (value.length > 3) {
        const q = value.replace(/\s/g, "_").toLowerCase();
        const filtered = searchCache.filter((p) => p.player_id.includes(q));
        setSearchOptions(filtered.filter((p) => !rankedIds.has(p.player_id)));
      } else {
        setSearchOptions([]);
      }
    }
  };

  const addPlayer = async (player) => {
    if (!player || rankedIds.has(player.player_id)) return;
    let fullPlayer = player;
    try {
      const { data } = await publicApi.get(`/players/${player.player_id}`);
      fullPlayer = data;
    } catch {
      // use search result if detail fetch fails
    }
    setEntries((prev) => [...prev, makeEntry(fullPlayer)]);
    setSearchInput("");
    setSearchOptions([]);
    setAddPlayerOpen(true);
  };

  const closeAddPlayer = () => {
    setAddPlayerOpen(false);
    setSearchInput("");
    setSearchOptions([]);
  };

  const removeEntry = (index) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const reorderEntries = (fromIndex, toIndex) => {
    if (fromIndex == null || toIndex == null || fromIndex === toIndex) return;
    setEntries((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const findDropIndexFromPoint = useCallback((clientX, clientY) => {
    const count = entries.length;
    for (let i = 0; i < count; i += 1) {
      const el = entryRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        return i;
      }
    }
    let nearest = null;
    let nearestDist = Infinity;
    for (let i = 0; i < count; i += 1) {
      const el = entryRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const dist = Math.abs(clientY - centerY);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = i;
      }
    }
    return nearest;
  }, [entries.length]);

  const resetTouchDrag = () => {
    touchPointerRef.current = {
      active: false,
      fromIndex: null,
      pointerId: null,
      startX: 0,
      startY: 0,
      moved: false,
    };
    dragIndexRef.current = null;
    dragOverIndexRef.current = null;
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const isInteractiveDragTarget = (target) =>
    Boolean(
      target?.closest?.(
        "button, textarea, input, select, .MuiIconButton-root, .MuiFormControl-root, .create-ranking-entry-delete, .create-ranking-note, .create-ranking-traits"
      )
    );

  const handleDragStart = (index) => (e) => {
    if (isInteractiveDragTarget(e.target)) {
      e.preventDefault();
      return;
    }
    const entryEl = entryRefs.current[index];
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
    if (entryEl) {
      const rect = entryEl.getBoundingClientRect();
      const offsetX = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
      const offsetY = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
      e.dataTransfer.setDragImage(entryEl, offsetX, offsetY);
    }
    requestAnimationFrame(() => setDragIndex(index));
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const from = dragIndexRef.current;
    if (from != null && from !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (index) => (e) => {
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    setDragOverIndex((prev) => (prev === index ? null : prev));
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fromRaw =
      dragIndexRef.current ?? e.dataTransfer.getData("text/plain");
    const from = Number.parseInt(String(fromRaw), 10);
    if (!Number.isNaN(from)) {
      reorderEntries(from, index);
    }
    dragIndexRef.current = null;
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleListDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handlePointerDown = (index) => (e) => {
    if (e.pointerType !== "touch") return;
    if (isInteractiveDragTarget(e.target)) return;

    touchPointerRef.current = {
      active: true,
      fromIndex: index,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    dragIndexRef.current = index;
    dragOverIndexRef.current = null;
    setDragIndex(index);
    setDragOverIndex(null);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = () => (e) => {
    const state = touchPointerRef.current;
    if (!state.active || state.pointerId !== e.pointerId) return;

    const dx = Math.abs(e.clientX - state.startX);
    const dy = Math.abs(e.clientY - state.startY);
    if (!state.moved) {
      if (dx < POINTER_DRAG_THRESHOLD && dy < POINTER_DRAG_THRESHOLD) return;
      state.moved = true;
    }

    e.preventDefault();
    const over = findDropIndexFromPoint(e.clientX, e.clientY);
    if (over != null && over !== state.fromIndex) {
      dragOverIndexRef.current = over;
      setDragOverIndex(over);
    }
  };

  const handlePointerUp = () => (e) => {
    const state = touchPointerRef.current;
    if (!state.active || state.pointerId !== e.pointerId) return;

    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    if (state.moved) {
      const from = state.fromIndex;
      const to = dragOverIndexRef.current;
      if (from != null && to != null && from !== to) {
        reorderEntries(from, to);
      }
    }

    resetTouchDrag();
  };

  const updateEntry = (index, patch) => {
    setEntries((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const buildPayload = useCallback(
    (publish) => {
      const players = entries.map((entry, index) => {
        const row = {
          player_id: entry.player_id,
          rank: index + 1,
        };
        if (includeNotes && entry.note?.trim()) {
          row.note = entry.note.trim();
        }
        if (includeAttributes) {
          traitFieldsForPlayer(entry).forEach(({ key }) => {
            const raw = entry[key];
            if (raw !== "" && raw != null) {
              row[key] = Number(raw);
            }
          });
        }
        return row;
      });
      return {
        title: title.trim(),
        description: description.trim() || null,
        draft_year: String(rankingDraftYear),
        draft: !publish,
        public: publish,
        players,
      };
    },
    [entries, includeAttributes, includeNotes, title, description, rankingDraftYear]
  );

  const validateForm = () => {
    setError("");
    setSuccess("");
    if (!title.trim()) {
      setError("Please enter a title for your ranking.");
      return false;
    }
    if (entries.length === 0) {
      setError("Add at least one player to your ranking.");
      return false;
    }
    if (includeAttributes) {
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        for (const { key, label } of traitFieldsForPlayer(entry)) {
          const raw = entry[key];
          if (raw === "" || raw == null) continue;
          const n = Number(raw);
          if (
            !Number.isInteger(n) ||
            n < TRAIT_SCORE_MIN ||
            n > TRAIT_SCORE_MAX
          ) {
            setError(
              `#${i + 1} ${entry.last_name}: ${label} must be a whole number from ${TRAIT_SCORE_MIN} to ${TRAIT_SCORE_MAX}.`
            );
            return false;
          }
        }
      }
    }
    return true;
  };

  const hasUnsavedWork = useMemo(() => {
    if (title.trim() || description.trim()) return true;
    if (entries.length > 0) return true;
    if (entries.some((e) => e.note?.trim())) return true;
    if (entries.some((e) => ALL_TRAIT_KEYS.some((key) => e[key] !== "" && e[key] != null))) {
      return true;
    }
    return false;
  }, [title, description, entries]);

  const hasEditChanges = useMemo(() => {
    if (!isEdit || !loadedSnapshotRef.current) return false;
    return (
      formSnapshot(title, description, rankingDraftYear, includeNotes, includeAttributes, entries) !==
      loadedSnapshotRef.current
    );
  }, [isEdit, title, description, rankingDraftYear, includeNotes, includeAttributes, entries]);

  const { allowLeave } = useWarnUnsavedChanges(isEdit ? hasEditChanges : hasUnsavedWork);

  const saveRanking = async (publish) => {
    if (!validateForm()) return;
    setSaving(true);
    setSaveMode(publish ? "publish" : "draft");
    try {
      if (isEdit) {
        await authAxios.put(`/independent_rankings/${rankingId}`, buildPayload(publish));
      } else {
        await authAxios.post("/independent_rankings", buildPayload(publish));
      }
      allowLeave();
      if (publish) {
        setPublishConfirmOpen(false);
      }
      setSuccess(
        publish
          ? "Ranking published."
          : isEdit
            ? "Ranking updated."
            : "Ranking saved as a draft."
      );
      loadedSnapshotRef.current = formSnapshot(
        title,
        description,
        rankingDraftYear,
        includeNotes,
        includeAttributes,
        entries
      );
      setTimeout(() => navigate("/your-rankings"), 600);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d) => d.msg || JSON.stringify(d)).join(" "));
      } else {
        setError(
          detail ||
            (publish
              ? "Failed to publish ranking. Please try again."
              : isEdit
                ? "Failed to update ranking. Please try again."
                : "Failed to save ranking. Please try again.")
        );
      }
    } finally {
      setSaving(false);
      setSaveMode(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveRanking(false);
  };

  const requestPublish = () => {
    if (!validateForm()) return;
    setPublishConfirmOpen(true);
  };

  const handlePublishConfirm = () => {
    saveRanking(true);
  };

  const handlePublishCancel = () => {
    if (saving) return;
    setPublishConfirmOpen(false);
  };

  const deleteRanking = async () => {
    if (!isEdit || !rankingId) return;
    const confirmed = window.confirm(
      `Delete "${title || "this ranking"}"? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeleting(true);
    setError("");
    try {
      await authAxios.delete(`/independent_rankings/${rankingId}`);
      allowLeave();
      navigate("/your-rankings");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Failed to delete ranking. Please try again."
      );
    } finally {
      setDeleting(false);
    }
  };

  const cardsOnlyLayout = !includeNotes && !includeAttributes;

  const renderEntryTraits = (entry, index) => (
    <Box
      className={[
        "create-ranking-traits",
        !isGoalie(entry) ? "create-ranking-traits--skater" : "create-ranking-traits--goalie",
      ].join(" ")}
    >
      {traitFieldsForPlayer(entry).map(({ key, label }) => (
        <TextField
          key={key}
          label={label}
          type="number"
          value={entry[key]}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") {
              updateEntry(index, { [key]: "" });
              return;
            }
            if (!/^\d+$/.test(v)) return;
            const n = Number(v);
            if (n > TRAIT_SCORE_MAX) return;
            updateEntry(index, { [key]: v });
          }}
          inputProps={{
            min: TRAIT_SCORE_MIN,
            max: TRAIT_SCORE_MAX,
            step: 1,
          }}
          size="small"
          sx={fieldSx}
        />
      ))}
    </Box>
  );

  const entriesHaveNotes = entries.some((e) => Boolean(e.note?.trim()));
  const entriesHaveAttributes = entries.some((e) =>
    ALL_TRAIT_KEYS.some((key) => e[key] !== "" && e[key] != null)
  );

  const handleIncludeNotesChange = (checked) => {
    if (!checked && includeNotes && entriesHaveNotes) {
      const proceed = window.confirm(
        "Turning off Include notes will delete all notes you have entered for players in this ranking. Do you want to continue?"
      );
      if (!proceed) return;
      setEntries((prev) => prev.map((e) => ({ ...e, note: "" })));
    }
    setIncludeNotes(checked);
  };

  const handleIncludeAttributesChange = (checked) => {
    if (!checked && includeAttributes && entriesHaveAttributes) {
      const proceed = window.confirm(
        "Turning off Include attributes will delete all attribute scores you have entered for players in this ranking. Do you want to continue?"
      );
      if (!proceed) return;
      const cleared = emptyTraitScores();
      setEntries((prev) => prev.map((e) => ({ ...e, ...cleared })));
    }
    setIncludeAttributes(checked);
  };

  const clearRankedPlayers = () => {
    setEntries([]);
    setSearchInput("");
    setSearchOptions([]);
    setSearchCache([]);
    setDragIndex(null);
    setDragOverIndex(null);
    dragIndexRef.current = null;
    entryRefs.current = {};
  };

  const handleRankingDraftYearChange = (newYear) => {
    const nextYear = Number(newYear);
    if (nextYear === rankingDraftYear) return;

    if (entries.length > 0) {
      const proceed = window.confirm(
        "Changing the draft year will remove all players from your ranking. Do you want to continue?"
      );
      if (!proceed) return;
      clearRankedPlayers();
    }

    setRankingDraftYear(nextYear);
  };


  if (loadError) {
    return (
      <>
        <Helmet>
          <title>Edit Ranking - Hockey Pipeline</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Container className="container top-container">
          <Typography variant="h4" component="div" className="page-title">
            Edit Ranking
          </Typography>
          <div className="title-underline" />
          <Alert severity="error" sx={{ mt: 2 }}>
            {loadError}
          </Alert>
          <Button component={Link} to="/your-rankings" variant="outlined" size="small" sx={{ mt: 2 }}>
            Back to your rankings
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? "Edit Ranking" : "Create Ranking"} - Hockey Pipeline</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageLoader loading={loadingRanking} />
      <div className="create-ranking-shell top-container">
        <Container className="container create-ranking-page">
        <form onSubmit={handleSubmit}>
          <Grid container spacing={1.5} className="create-ranking-form-grid">
            <Grid item xs={12} className="create-ranking-page-header">
              <Typography variant="h4" component="div" className="page-title">
                {isEdit ? "Edit Ranking" : "Create Ranking"}
              </Typography>
              <div className="title-underline" />
            </Grid>

            <Grid item xs={12} className="create-ranking-meta-row">
              <div className="create-ranking-meta">
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  size="small"
                  margin="none"
                  className="create-ranking-title"
                  sx={{ ...fieldSx, my: 0 }}
                />
                <FormControl size="small" margin="none" className="create-ranking-year" sx={{ my: 0 }}>
                  <InputLabel sx={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                    Draft year
                  </InputLabel>
                  <Select
                    value={rankingDraftYear}
                    label="Draft year"
                    onChange={(e) => handleRankingDraftYearChange(e.target.value)}
                    sx={selectSx}
                    MenuProps={selectMenuProps}
                  >
                    {DRAFT_YEARS.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </Grid>

            <Grid item xs={12} className="create-ranking-meta-fields">
              <TextField
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                size="small"
                sx={fieldSx}
              />
              <div className="create-ranking-switches">
                <FormControlLabel
                  className="create-ranking-switch-pill"
                  labelPlacement="start"
                  label="Include notes"
                  control={
                    <Switch
                      size="small"
                      checked={includeNotes}
                      onChange={(e) => handleIncludeNotesChange(e.target.checked)}
                      sx={switchSx}
                    />
                  }
                />
                <FormControlLabel
                  className="create-ranking-switch-pill"
                  labelPlacement="start"
                  label="Include attributes"
                  control={
                    <Switch
                      size="small"
                      checked={includeAttributes}
                      onChange={(e) => handleIncludeAttributesChange(e.target.checked)}
                      sx={switchSx}
                    />
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} className="create-ranking-scale-mobile">
              <div className="create-ranking-scale-mobile-panel">
                <RatingScaleGuide
                  collapsible
                  open={mobileScaleGuideOpen}
                  onToggle={() => setMobileScaleGuideOpen((open) => !open)}
                />
              </div>
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ py: 0.25 }}>
                  {error}
                </Alert>
              </Grid>
            )}
            {success && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ py: 0.25 }}>
                  {success}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <div className="create-ranking-add-section">
                {!addPlayerOpen ? (
                  <button
                    type="button"
                    className="create-ranking-add-trigger"
                    onClick={() => setAddPlayerOpen(true)}
                    aria-label="Add player to ranking"
                  >
                    <span className="create-ranking-add-icon" aria-hidden>
                      <AddIcon />
                    </span>
                    <span className="create-ranking-add-copy">
                      <span className="create-ranking-add-title">Add player</span>
                      <span className="create-ranking-add-hint">
                        Search prospects by name to rank them
                      </span>
                    </span>
                  </button>
                ) : (
                  <div className="create-ranking-add-panel">
                    <div className="create-ranking-add-panel-head">
                      <Typography className="create-ranking-add-panel-label">
                        Add player
                      </Typography>
                      <IconButton
                        type="button"
                        size="small"
                        onClick={closeAddPlayer}
                        aria-label="Close add player"
                        sx={{ color: "var(--text-muted)" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>
                <Autocomplete
                  className="create-ranking-search"
                  options={searchOptions}
                  inputValue={searchInput}
                  onInputChange={handleSearchInput}
                  getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
                  onChange={(_, option) => addPlayer(option)}
                  value={null}
                  blurOnSelect
                  clearOnBlur
                  forcePopupIcon={false}
                  size="small"
                  openOnFocus
                      noOptionsText={
                        searchInput.length < 3
                          ? "Type at least 3 characters"
                          : `No ${rankingDraftYear} prospects found`
                      }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoFocus
                      placeholder="e.g. Macklin Celebrini"
                      sx={fieldSx}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <span className="create-ranking-search-prefix">
                            <AddIcon sx={{ fontSize: 18 }} />
                          </span>
                        ),
                      }}
                    />
                  )}
                />
                    <Typography variant="caption" className="create-ranking-add-tip">
                      Searching {rankingDraftYear} prospects · select to add at #{entries.length + 1}
                    </Typography>
                  </div>
                )}
              </div>
              {entries.length > 0 && (
                <Typography variant="caption" sx={{ color: "var(--text-muted)", mt: 0.75, display: "block" }}>
                  {entries.length} player{entries.length === 1 ? "" : "s"} ranked ·{" "}
                  {cardsOnlyLayout ? "drag cards to reorder" : "drag rows to reorder"}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <div
                className={[
                  "create-ranking-list",
                  cardsOnlyLayout ? "create-ranking-list--cards-only" : "",
                  dragIndex != null ? "is-dragging-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onDragOver={handleListDragOver}
              >
                {entries.map((entry, index) => (
                  <div
                    key={entry.player_id}
                    ref={(el) => {
                      if (el) entryRefs.current[index] = el;
                      else delete entryRefs.current[index];
                    }}
                    draggable
                    className={[
                      "create-ranking-entry",
                      cardsOnlyLayout
                        ? "create-ranking-entry--card-only"
                        : "create-ranking-entry--detail",
                      dragIndex === index ? "is-dragging" : "",
                      dragOverIndex === index && dragIndex !== index ? "drag-over" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onDragStart={handleDragStart(index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver(index)}
                    onDragLeave={handleDragLeave(index)}
                    onDrop={handleDrop(index)}
                    onPointerDown={handlePointerDown(index)}
                    onPointerMove={handlePointerMove()}
                    onPointerUp={handlePointerUp()}
                    onPointerCancel={handlePointerUp()}
                  >
                    {cardsOnlyLayout && (
                      <IconButton
                        type="button"
                        size="small"
                        className="create-ranking-entry-remove"
                        onClick={() => removeEntry(index)}
                        aria-label={`Remove ${entry.player?.first_name} ${entry.player?.last_name}`}
                        sx={{ color: "var(--negative)" }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                    {cardsOnlyLayout ? (
                      <div className="create-ranking-entry-card-wrap">
                        <PlayerCard
                          playerData={entry.player}
                          ranking={index + 1}
                          miniView={Boolean(entry.player?.dob)}
                          shortView={!entry.player?.dob}
                          disableLink
                        />
                      </div>
                    ) : (
                      <>
                        <IconButton
                          type="button"
                          size="small"
                          className="create-ranking-entry-delete"
                          onClick={() => removeEntry(index)}
                          aria-label={`Remove ${entry.player?.first_name} ${entry.player?.last_name}`}
                          sx={{ color: "var(--negative)" }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <div className="create-ranking-entry-body">
                          <div className="create-ranking-entry-top">
                            <div className="create-ranking-entry-card-wrap">
                              <PlayerCard
                                playerData={entry.player}
                                ranking={index + 1}
                                miniView={Boolean(entry.player?.dob)}
                                shortView={!entry.player?.dob}
                                disableLink
                              />
                            </div>
                            {includeNotes && (
                              <TextField
                                label="Note"
                                placeholder="Scouting notes for this player…"
                                value={entry.note}
                                onChange={(e) => updateEntry(index, { note: e.target.value })}
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={8}
                                size="small"
                                className="create-ranking-note"
                                sx={fieldSx}
                              />
                            )}
                            {includeAttributes && !includeNotes && renderEntryTraits(entry, index)}
                          </div>
                          {includeAttributes && includeNotes && renderEntryTraits(entry, index)}
                        </div>
                        <div
                          className="create-ranking-drag-handle create-ranking-drag-handle--bar"
                          aria-label={`Drag to reorder rank ${index + 1}`}
                          title="Drag to reorder"
                        >
                          <DragIndicatorIcon sx={{ fontSize: 14, transform: "rotate(90deg)" }} />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Grid>

            <Grid item xs={12}>
              <Box className="create-ranking-actions">
                {isEdit && (
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    className="create-ranking-btn create-ranking-btn--delete"
                    disabled={saving || deleting}
                    onClick={deleteRanking}
                  >
                    {deleting ? "Deleting…" : "Delete"}
                  </Button>
                )}
                <Box className="create-ranking-actions-primary">
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    className="create-ranking-btn create-ranking-btn--draft"
                    disabled={saving || deleting}
                    onClick={() => saveRanking(false)}
                  >
                    {saving && saveMode === "draft" ? "Saving…" : "Save draft"}
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    size="small"
                    className="create-ranking-btn create-ranking-btn--publish"
                    disabled={saving || deleting}
                    onClick={requestPublish}
                  >
                    {saving && saveMode === "publish" ? "Publishing…" : "Publish"}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
        </Container>
        <ConfirmDialog
          open={publishConfirmOpen}
          title="Publish ranking?"
          confirmLabel="Publish"
          cancelLabel="Cancel"
          onConfirm={handlePublishConfirm}
          onCancel={handlePublishCancel}
          loading={saving && saveMode === "publish"}
        >
          <p id="site-dialog-description" className="site-dialog-message">
            Once you publish this ranking,{" "}
            <strong style={{ textDecoration: "underline" }}>
              it cannot be edited or deleted
            </strong>
            . It will be visible on the site according to your ranking settings.
            Are you sure you want to publish?
          </p>
        </ConfirmDialog>
        <aside className="create-ranking-gutter" aria-label="Attribute rating scale">
          <div className="create-ranking-gutter-panel">
            <RatingScaleGuide />
          </div>
        </aside>
      </div>
    </>
  );
}

function CreateRanking() {
  return (
    <RequireAuth>
      <CreateRankingContent />
    </RequireAuth>
  );
}

export default CreateRanking;
